import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X, Home, Wrench, Euro, HelpCircle, Package, Phone, Globe, ChevronDown
} from 'lucide-react';
import { useLanguage, Language } from '../context/LanguageContext';
import { getPath, getEquivalentPath } from '../config/routes';

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  const languages: { code: Language; label: string; flag: string }[] = [
    { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
  ];

  const navItems = [
    { key: 'home', labelKey: 'nav.home', icon: <Home className="w-4 h-4" /> },
    { key: 'services', labelKey: 'nav.services', icon: <Wrench className="w-4 h-4" /> },
    { key: 'pricing', labelKey: 'nav.pricing', icon: <Euro className="w-4 h-4" /> },
    { key: 'faq', labelKey: 'nav.faq', icon: <HelpCircle className="w-4 h-4" /> },
    { key: 'shipping', labelKey: 'nav.shipping', icon: <Package className="w-4 h-4" /> },
    { key: 'contact', labelKey: 'nav.contact', icon: <Phone className="w-4 h-4" /> },
  ];

  // Handle language change - navigate to equivalent page
  const handleLanguageChange = (newLang: Language) => {
    const newPath = getEquivalentPath(location.pathname, newLang);
    setLanguage(newLang);
    navigate(newPath);
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const isActive = (key: string) => {
    const path = getPath(key, language);
    return location.pathname === path;
  };

  const isHomePage = location.pathname === getPath('home', language) || location.pathname === '/';

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-lg py-3'
          : isHomePage
            ? 'bg-gradient-to-b from-gray-900/50 to-transparent backdrop-blur-sm py-5'
            : 'bg-white shadow-md py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to={getPath('home', language)} className="flex items-center space-x-3 group">
              <div className="relative w-11 h-11 transform transition-all duration-300 group-hover:scale-110">
                <img src="/logo.png" alt="Pirkanmaan Potkurikorjaus" className="w-full h-full object-contain" />
              </div>
              <div>
                <span className={`text-xl font-bold transition-colors duration-300 ${
                  isScrolled || !isHomePage ? 'text-gray-900' : 'text-white'
                }`}>
                  Pirkanmaan Potkurikorjaus
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={getPath(item.key, language)}
                  className={`relative px-4 py-2 font-medium transition-all duration-300 rounded-lg group flex items-center gap-2 ${
                    isActive(item.key)
                      ? isScrolled || !isHomePage
                        ? 'text-cyan-600 bg-cyan-50'
                        : 'text-white bg-white/10'
                      : isScrolled || !isHomePage
                        ? 'text-gray-700 hover:text-cyan-600 hover:bg-gray-50'
                        : 'text-gray-200 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span className="hidden xl:block">{item.icon}</span>
                  {t(item.labelKey)}
                </Link>
              ))}

              {/* Language Switcher */}
              <div className="relative ml-2">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium transition-all duration-300 ${
                    isScrolled || !isHomePage
                      ? 'text-gray-700 hover:text-cyan-600 hover:bg-gray-50'
                      : 'text-gray-200 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm">{languages.find(l => l.code === language)?.flag}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLangMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsLangMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-20">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                            language === lang.code
                              ? 'bg-cyan-50 text-cyan-600'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span>{lang.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden relative p-2.5 rounded-lg transition-all duration-300 ${
                isScrolled || !isHomePage
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-180 opacity-0' : 'rotate-0 opacity-100'
                }`}>
                  <Menu className="w-6 h-6" />
                </span>
                <span className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-0 opacity-100' : '-rotate-180 opacity-0'
                }`}>
                  <X className="w-6 h-6" />
                </span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
        isMobileMenuOpen ? 'visible' : 'invisible'
      }`}>
        <div
          className={`absolute inset-0 bg-gray-900 transition-opacity duration-500 ${
            isMobileMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <div className={`absolute right-0 top-0 h-full w-full sm:w-80 bg-white shadow-2xl transition-transform duration-500 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full pt-20 px-6 pb-6">
            <nav className="flex-1">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={getPath(item.key, language)}
                  className={`flex items-center gap-3 py-3 text-lg font-medium transition-all duration-300 ${
                    isActive(item.key)
                      ? 'text-cyan-600'
                      : 'text-gray-700 hover:text-cyan-600'
                  }`}
                >
                  {item.icon}
                  {t(item.labelKey)}
                </Link>
              ))}

              {/* Mobile Language Switcher */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {language === 'fi' ? 'Kieli' : language === 'sv' ? 'Språk' : 'Language'}
                </p>
                <div className="flex gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        language === lang.code
                          ? 'bg-cyan-100 text-cyan-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>
            </nav>

            <a
              href="tel:+358400416466"
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              {t('nav.callNow')}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
